import { useMemo, useState } from "react";
import { Package } from "lucide-react";
import { useManagePlans, useFeatures, useSetPlanActive } from "@/hooks/usePlans";
import { PlanFormModal } from "@/components/PlanFormModal";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ManagePlan } from "@/lib/api";

const TH =
  "text-[11px] font-semibold uppercase tracking-widest text-ink-500";

export function PlansList(): React.JSX.Element {
  const { data: plans, isPending, isError } = useManagePlans();
  const { data: features = [] } = useFeatures();
  const setActive = useSetPlanActive();

  // Modal state: create (plan=null) or edit (plan set). Separate disable target.
  const [formOpen, setFormOpen] = useState(false);
  const [formPlan, setFormPlan] = useState<ManagePlan | null>(null);
  const [disableTarget, setDisableTarget] = useState<ManagePlan | null>(null);

  const featureName = useMemo(() => {
    const map = new Map(features.map((f) => [f.id, f.name]));
    return (id: string): string => map.get(id) ?? id;
  }, [features]);

  function openCreate(): void {
    setFormPlan(null);
    setFormOpen(true);
  }

  function openEdit(plan: ManagePlan): void {
    setFormPlan(plan);
    setFormOpen(true);
  }

  function confirmDisable(): void {
    if (!disableTarget) return;
    setActive.mutate({ id: disableTarget.id, isActive: false });
    setDisableTarget(null);
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-18 flex-shrink-0 items-center justify-between border-b border-ink-100 bg-surface px-6 py-4">
        <div>
          <h1 className="font-display text-[28px] font-extrabold leading-none text-ink-900">
            Planes
          </h1>
          <p className="mt-0.5 text-xs text-ink-500">
            Planes de suscripción y las funciones que otorgan.
          </p>
        </div>
        <Button size="sm" onClick={openCreate}>
          + Nuevo plan
        </Button>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {isError ? (
          <p className="text-sm text-[oklch(0.55_0.18_16)]">
            No se pudieron cargar los planes.
          </p>
        ) : isPending ? (
          <p className="text-sm text-ink-500">Cargando…</p>
        ) : plans.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
              <Package size={22} className="text-ink-400" />
            </div>
            <p className="font-display text-lg font-bold text-ink-700">
              Aún no hay planes
            </p>
            <Button size="sm" onClick={openCreate} className="mt-1">
              Crear el primer plan
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-ink-100 bg-surface shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-ink-050 hover:bg-ink-050">
                  <TableHead className={TH}>Nombre</TableHead>
                  <TableHead className={TH}>Clave</TableHead>
                  <TableHead className={TH}>Pos.</TableHead>
                  <TableHead className={TH}>Funciones</TableHead>
                  <TableHead className={TH}>Clientes</TableHead>
                  <TableHead className={TH}>Estado</TableHead>
                  <TableHead className={`text-right ${TH}`}>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow
                    key={plan.id}
                    className={plan.isActive ? "hover:bg-ink-050" : "opacity-55"}
                  >
                    <TableCell className="text-xs font-medium text-ink-900">
                      {plan.name}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-ink-700">
                      {plan.key}
                    </TableCell>
                    <TableCell className="text-xs text-ink-700">{plan.position}</TableCell>
                    <TableCell className="text-xs text-ink-700">
                      {plan.featureIds.length === 0 ? (
                        <span className="text-ink-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {plan.featureIds.map((id) => (
                            <span
                              key={id}
                              className="inline-flex rounded-full bg-lime/25 px-2 py-0.5 text-[11px] font-medium text-moss"
                            >
                              {featureName(id)}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-ink-700">{plan.clientCount}</TableCell>
                    <TableCell>
                      {plan.isActive ? (
                        <span className="inline-flex rounded-full bg-[oklch(0.94_0.04_155)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.30_0.10_155)]">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-500">
                          Deshabilitado
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => openEdit(plan)}>
                          Editar
                        </Button>
                        {plan.isActive ? (
                          <button
                            onClick={() => setDisableTarget(plan)}
                            className="rounded border border-[oklch(0.84_0.07_16)] px-2.5 py-1 text-xs font-medium text-[oklch(0.40_0.14_16)] transition hover:bg-[oklch(0.96_0.025_16)]"
                          >
                            Deshabilitar
                          </button>
                        ) : (
                          <button
                            onClick={() => setActive.mutate({ id: plan.id, isActive: true })}
                            disabled={setActive.isPending}
                            className="rounded border border-[oklch(0.84_0.06_155)] px-2.5 py-1 text-xs font-medium text-[oklch(0.30_0.10_155)] transition hover:bg-[oklch(0.94_0.04_155)] disabled:opacity-50"
                          >
                            Habilitar
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <PlanFormModal open={formOpen} onOpenChange={setFormOpen} plan={formPlan} />

      <AlertDialog
        open={disableTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDisableTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Deshabilitar “{disableTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Ya no podrás asignar este plan a nuevos clientes. Los{" "}
              {disableTarget?.clientCount ?? 0} cliente(s) que ya lo tienen no se
              verán afectados y conservarán su plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisable}>Deshabilitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
